// The awake hour: what to do with him between naps, as steps that tick off
// from the log. Wee on waking → food (if a meal falls in this window) → one
// short active thing → calm time near you → wee, then his crate for a nap.
// Which step is "now" comes from what's been logged since he woke and how far
// through his awake window he is.

import { MINUTE, formatTime } from './time.ts';
import type { LogEvent, Millis } from './types.ts';

export type AwakeStepId = 'wee' | 'meal' | 'out' | 'active' | 'calm' | 'nap';
export type AwakeStepState = 'done' | 'now' | 'next' | 'skipped';

export interface AwakeStep {
  id: AwakeStepId;
  title: string;
  detail: string;
  /** Things to pick from, for the active and calm steps. */
  ideas?: string[];
  state: AwakeStepState;
  /** When it was done (from the log). */
  doneAt?: Millis;
  /** Nap step only: he's been out in the last 20 minutes, so no wee needed first. */
  beenOut?: Millis;
}

export interface AwakePlan {
  steps: AwakeStep[];
  current: AwakeStepId;
  wokeAt: Millis;
  /** When the nap's due (end of his awake window). */
  napAt: Millis;
  /** 0–1 through his awake window. */
  progress: number;
}

export interface AwakePlanInput {
  now: Millis;
  wokeAt: Millis;
  /** His awake window in minutes (domain/age.ts). */
  windowMin: number;
  events: LogEvent[];
  /** When the next (or a missed) meal is due, if any. */
  mealDueAt?: Millis;
  /** Where he sleeps: "crate" or "pen". */
  den: string;
  /** Allowed out in public yet (the vet's go-out date has passed)? */
  canGoOut?: boolean;
  /** The toilet prediction says a trip is due now (e.g. after his meal), with its reason. */
  toiletDue?: { reason: string };
}

/** Toilet, food and activity types that count for each step. */
const ACTIVE: LogEvent['type'][] = ['training', 'play', 'social', 'walk'];

export function awakePlan(x: AwakePlanInput): AwakePlan {
  const { now, wokeAt, windowMin, events, den } = x;
  const napAt = wokeAt + windowMin * MINUTE;
  const awake = now - wokeAt;
  const progress = Math.min(1, Math.max(0, awake / (windowMin * MINUTE)));
  // A minute's grace: a wee logged as he was waking still counts.
  const since = events.filter((e) => e.at >= wokeAt - 2 * MINUTE && e.at <= now);
  const first = (pred: (e: LogEvent) => boolean) => since.filter(pred).sort((a, b) => a.at - b.at)[0]?.at;

  const weeAt = first((e) => e.type === 'wee' || e.type === 'poo');
  // The latest trip out: within 20 minutes, the nap doesn't need another wee first.
  const lastOut = since.filter((e) => e.type === 'wee' || e.type === 'poo').reduce<Millis | undefined>((m, e) => (m === undefined || e.at > m ? e.at : m), undefined);
  const beenOut = lastOut !== undefined && now - lastOut < 20 * MINUTE ? lastOut : undefined;
  const mealAt = first((e) => e.type === 'meal');
  const activeAt = first((e) => ACTIVE.includes(e.type));
  // A meal belongs in this window if one's been eaten, or one's due before the nap.
  const mealHere = mealAt !== undefined || (x.mealDueAt !== undefined && x.mealDueAt <= napAt);
  // Near the end of the window it's wind-down time, whatever's left.
  const napTime = now >= napAt - 10 * MINUTE;
  // Most of the way through without a game: that's fine, go calm.
  const activeLapsed = activeAt === undefined && progress >= 0.6;

  const steps: Omit<AwakeStep, 'state'>[] = [
    { id: 'wee', title: 'Wee on waking', detail: 'Straight out — carry him so there’s no accident on the way.', doneAt: weeAt },
    ...(mealHere ? [{ id: 'meal' as const, title: 'Food', detail: 'Keep back a handful of his kibble for training and the games below.', doneAt: mealAt }] : []),
    {
      id: 'active',
      title: 'One short active thing · 10–15 min',
      detail: 'Pick one, and stop while he still wants more.',
      ideas: [
        'A 2–3 minute training session: his name, sit or a hand touch',
        'A gentle toy game — swap it for a treat now and then',
        'Paws and ears handled, a treat for each touch',
        'Something new at home: the hoover (off, then on), a new surface, a visitor',
        ...(x.canGoOut ? ['A short trip out to sit and watch the world'] : []),
      ],
      doneAt: activeAt,
    },
    {
      id: 'calm',
      title: 'Calm time near you',
      detail: 'Let him potter about the living area while one of you keeps an eye on him — no need to entertain him. Can’t watch? Pen with a stuffed Kong.',
      ideas: [
        'Scatter a little kibble to sniff out',
        'A lick mat, snuffle mat or chew',
        'A cardboard box or loo-roll tube with kibble in to shred',
        'He lies down calmly? Quietly drop a treat between his paws',
      ],
    },
    beenOut !== undefined
      ? {
          id: 'nap',
          title: `His ${den} for a nap`,
          detail: `He’s just been out (${formatTime(beenOut)}), so straight into his ${den} with a chew, lights low. Bitey or giddy is the sign he’s tired.`,
          beenOut,
        }
      : {
          id: 'nap',
          title: `Wee, then his ${den} for a nap`,
          detail: `Bitey or giddy is the sign he’s tired. Out for a wee, then into his ${den} with a chew, lights low.`,
        },
  ];

  // A trip out the toilet prediction says is due (after food, play…) comes before
  // anything else — the same call as the Now card — once the waking wee is done.
  const tripDue = !napTime && weeAt !== undefined && x.toiletDue !== undefined;
  // The wee that follows a meal, once it's happened.
  const weeAfterMeal = mealAt !== undefined ? first((e) => (e.type === 'wee' || e.type === 'poo') && e.at > mealAt) : undefined;
  if (tripDue) {
    const reason = x.toiletDue!.reason.replace(/^./, (c) => c.toLowerCase());
    const step = { id: 'out' as const, title: 'Trip out for a wee', detail: `Due now: ${reason}. Then carry on from here.` };
    // Where he's up to: before the first step not yet done.
    const at = steps.findIndex((s) => s.id !== 'wee' && s.doneAt === undefined);
    steps.splice(at >= 0 ? at : steps.length - 1, 0, step);
  } else if (weeAfterMeal !== undefined) {
    steps.splice(steps.findIndex((s) => s.id === 'meal') + 1, 0, { id: 'out', title: 'Wee after food', detail: '', doneAt: weeAfterMeal });
  }

  // The current step: wind-down near the end; otherwise the first thing not yet done.
  let current: AwakeStepId;
  if (napTime) current = 'nap';
  else if (weeAt === undefined) current = 'wee';
  else if (tripDue) current = 'out';
  else if (mealHere && mealAt === undefined && (x.mealDueAt === undefined || x.mealDueAt <= now + 10 * MINUTE)) current = 'meal';
  else if (activeAt === undefined && !activeLapsed) current = 'active';
  else current = 'calm';

  const order = steps.map((s) => s.id);
  const at = order.indexOf(current);
  const out: AwakeStep[] = steps.map((s, i) => {
    let state: AwakeStepState;
    if (s.id === current) state = 'now';
    else if (s.doneAt !== undefined) state = 'done';
    // A meal not yet eaten is still to come, never skipped.
    else if (s.id === 'meal') state = 'next';
    else if (i < at) state = s.id === 'calm' ? 'done' : 'skipped';
    else state = 'next';
    return { ...s, state };
  });
  return { steps: out, current, wokeAt, napAt, progress };
}

// --- Looking back: how the awake hours went (for the report) ---------------------

export interface AwakeSpell {
  woke: Millis;
  /** Back to sleep. */
  slept: Millis;
  minutes: number;
  /** A wee or poo within 15 minutes of waking. */
  weeOnWaking: boolean;
  /** Training, play, an outing, a new experience or a visit while he was up. */
  active: boolean;
  /** Over his awake window by more than a quarter of an hour. */
  over: boolean;
}

export interface AwakeReview {
  spells: AwakeSpell[];
  avgMinutes?: number;
  windowMin: number;
  over: number;
  weeOnWaking: number;
  active: number;
}

const ACTIVE_TYPES: LogEvent['type'][] = ['training', 'play', 'walk', 'social', 'visit'];

/**
 * Each daytime awake spell in [from, to): from waking to the next sleep (only
 * where both are logged and it's under four hours — longer is a gap in the
 * logs, not a puppy awake all afternoon), and whether the awake-hour steps
 * happened: a wee on waking, something active, back down before he was over.
 */
export function awakeReview(
  events: LogEvent[],
  from: Millis,
  to: Millis,
  windowMin: number,
  sleeps: [Millis, Millis][],
  isDay: (t: Millis) => boolean,
): AwakeReview {
  const spells: AwakeSpell[] = [];
  for (let i = 1; i < sleeps.length; i++) {
    const woke = sleeps[i - 1][1];
    const slept = sleeps[i][0];
    const minutes = (slept - woke) / MINUTE;
    if (woke < from || woke >= to || !isDay(woke) || minutes <= 5 || minutes >= 4 * 60) continue;
    const during = events.filter((e) => e.at >= woke - 2 * MINUTE && e.at < slept);
    spells.push({
      woke,
      slept,
      minutes,
      weeOnWaking: during.some((e) => (e.type === 'wee' || e.type === 'poo') && e.at <= woke + 15 * MINUTE),
      active: during.some((e) => ACTIVE_TYPES.includes(e.type)),
      over: minutes > windowMin + 15,
    });
  }
  return {
    spells,
    avgMinutes: spells.length ? spells.reduce((n, s) => n + s.minutes, 0) / spells.length : undefined,
    windowMin,
    over: spells.filter((s) => s.over).length,
    weeOnWaking: spells.filter((s) => s.weeOnWaking).length,
    active: spells.filter((s) => s.active).length,
  };
}
